import React, { useState, useEffect } from 'react';
import { getApiUrl } from '../config';
import { useNotification } from './Notification';
import { theme } from '../theme';
import { MatchReviews, PlayerReview, ReviewReactions as ReviewReactionsType } from '../types';
import { logger } from '../utils/logger';
import { useResponsive } from '../hooks/useResponsive';
import { useWebSocket } from '../hooks/useWebSocket';
import { formatDateShort, formatReviewTimestamp } from '../utils/helpers';
import { ReviewReactions } from './ReviewReactions';

interface ReviewsListProps {
  matchId: string;
  isVisible: boolean;
  onClose: () => void;
}

export const ReviewsList: React.FC<ReviewsListProps> = ({
  matchId,
  isVisible,
  onClose
}) => {

  const { isMobile } = useResponsive();
  const { notification, showNotification, hideNotification } = useNotification();
  
  const [reviews, setReviews] = useState<MatchReviews | null>(null);
  const [loading, setLoading] = useState(true);
  const [reactionsMap, setReactionsMap] = useState<{ [reviewId: string]: { reactions: ReviewReactionsType; totalReactions: number } }>({});
  const [hoveredReviewId, setHoveredReviewId] = useState<string | null>(null);

  // WebSocket para reacciones en tiempo real
  const { joinMatch, leaveMatch } = useWebSocket((event) => {
    if (event.type === 'REACTION_ADDED' || event.type === 'REACTION_REMOVED') {
      handleWebSocketReactionUpdate(event);
    }
  });

  useEffect(() => {
    if (isVisible && matchId) {
      fetchReviews();
      // Unirse al match para recibir actualizaciones en tiempo real
      joinMatch(matchId);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible, matchId, joinMatch]);

  // Salir del match cuando se cierre el componente
  useEffect(() => {
    return () => {
      if (matchId) {
        leaveMatch(matchId);
      }
    };
  }, [matchId, leaveMatch]);

  const fetchReviews = async () => {
    try {
      setLoading(true);
      const response = await fetch(getApiUrl(`/api/reviews/match/${matchId}`));
      
      if (response.ok) {
        const data = await response.json();
        setReviews(data);
      } else {
        const error = await response.json();
        showNotification(error.error || 'Error cargando reviews', 'error');
      }
    } catch (error) {
      logger.error('Error fetching reviews:', error);
      showNotification('Error de conexión', 'error');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return formatDateShort(dateString);
  };

  const formatReviewDate = (timestamp: string) => {
    return formatReviewTimestamp(timestamp);
  };

  const handleReactionsUpdate = (reviewId: string, reactions: ReviewReactionsType, totalReactions: number) => {
    setReactionsMap(prev => ({
      ...prev,
      [reviewId]: { reactions, totalReactions }
    }));
  };

  // Manejar actualizaciones de reacciones desde WebSocket
  const handleWebSocketReactionUpdate = (event: any) => {
    const { reviewId, emoji, userId, userName } = event.data;
    
    setReactionsMap(prev => {
      const currentReactions = prev[reviewId]?.reactions || {};
      let updatedReactions = { ...currentReactions };
      
      if (event.type === 'REACTION_ADDED') {
        // Agregar reacción
        if (!updatedReactions[emoji]) {
          updatedReactions[emoji] = [];
        }
        
        // Verificar si el usuario ya reaccionó
        const existingReaction = updatedReactions[emoji].find(r => r.userId === userId);
        if (!existingReaction) {
          updatedReactions[emoji].push({
            id: Date.now().toString(),
            userId,
            userName,
            emoji,
            createdAt: new Date().toISOString()
          });
        }
      } else if (event.type === 'REACTION_REMOVED') {
        // Remover reacción
        if (updatedReactions[emoji]) {
          updatedReactions[emoji] = updatedReactions[emoji].filter(r => r.userId !== userId);
          if (updatedReactions[emoji].length === 0) {
            delete updatedReactions[emoji];
          }
        }
      }
      
      const newTotalReactions = Object.values(updatedReactions).reduce(
        (total, reactions) => total + reactions.length, 0
      );
      
      return {
        ...prev,
        [reviewId]: { reactions: updatedReactions, totalReactions: newTotalReactions }
      };
    });
  };

  const getTagStyle = (tag: string, isPositive: boolean) => ({
    padding: '4px 8px',
    borderRadius: '12px',
    fontSize: '12px',
    fontFamily: theme.fonts.body,
    fontWeight: theme.fonts.weightBold,
    backgroundColor: isPositive ? theme.colors.success : theme.colors.danger,
    color: theme.colors.white,
    margin: '2px',
    display: 'inline-block'
  });

  if (!isVisible) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: isMobile ? '16px' : '32px'
    }}>
      <div style={{
        backgroundColor: theme.colors.surface,
        borderRadius: theme.borderRadius.card,
        padding: isMobile ? '16px' : theme.spacing.lg,
        maxWidth: isMobile ? '100%' : '800px',
        width: '100%',
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: theme.colors.shadow,
        border: `2px solid ${theme.colors.primary}`
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: theme.spacing.lg
        }}>
          <h2 style={{
            margin: 0,
            color: theme.colors.primary,
            fontFamily: theme.fonts.heading,
            fontSize: isMobile ? '20px' : theme.fonts.sizeTitle
          }}>
            Reviews del Partido
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: theme.colors.textSecondary
            }}
          >
            ×
          </button>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: theme.spacing.lg }}>
            Cargando reviews...
          </div>
        ) : reviews ? (
          <div>
            {/* Información del partido */}
            <div style={{
              backgroundColor: theme.colors.background,
              padding: theme.spacing.md,
              borderRadius: theme.borderRadius.card,
              marginBottom: theme.spacing.lg,
              border: `1px solid ${theme.colors.border}`
            }}>
              <h3 style={{
                margin: '0 0 8px 0',
                color: theme.colors.text,
                fontFamily: theme.fonts.body,
                fontSize: theme.fonts.sizeBody,
                fontWeight: theme.fonts.weightBold
              }}>
                📍 {reviews.match.location}
              </h3>
              <p style={{
                margin: '0 0 4px 0',
                color: theme.colors.textSecondary,
                fontFamily: theme.fonts.body,
                fontSize: theme.fonts.sizeSmall
              }}>
                📅 {formatDate(reviews.match.date)} - 🕐 {reviews.match.time}
              </p>
            </div>

            {/* Lista de reviews por jugador */}
                         {reviews.reviewsByPlayer.length === 0 ? (
               <div style={{
                 textAlign: 'center',
                 padding: theme.spacing.lg,
                 color: theme.colors.textSecondary,
                 fontFamily: theme.fonts.body
               }}>
                 No hay reviews aún para este partido
               </div>
            ) : (
              <div>
                {reviews.reviewsByPlayer.map((playerReviews, index) => (
                  <div key={playerReviews.player.id} style={{
                    marginBottom: theme.spacing.lg,
                    padding: theme.spacing.md,
                    backgroundColor: theme.colors.background,
                    borderRadius: theme.borderRadius.card,
                    border: `1px solid ${theme.colors.border}`
                  }}>
                    {/* Nombre del jugador */}
                    <h3 style={{
                      margin: '0 0 12px 0',
                      color: theme.colors.primary,
                      fontFamily: theme.fonts.body,
                      fontSize: theme.fonts.sizeBody,
                      fontWeight: theme.fonts.weightBold,
                      borderBottom: `2px solid ${theme.colors.primary}`,
                      paddingBottom: '8px'
                    }}>
                      👤 {playerReviews.player.name}
                    </h3>

                    {/* Reviews del jugador */}
                    {playerReviews.reviews.map((review) => (
                      <div key={review.id} style={{
                        marginBottom: theme.spacing.md,
                        padding: theme.spacing.md,
                        backgroundColor: theme.colors.surface,
                        borderRadius: theme.borderRadius.card,
                        border: `1px solid ${theme.colors.border}`,
                        position: 'relative',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                                             onMouseEnter={(e) => {
                         e.currentTarget.style.backgroundColor = theme.colors.background;
                         e.currentTarget.style.borderColor = theme.colors.primary;
                         setHoveredReviewId(review.id);
                       }}
                       onMouseLeave={(e) => {
                         e.currentTarget.style.backgroundColor = theme.colors.surface;
                         e.currentTarget.style.borderColor = theme.colors.border;
                         setHoveredReviewId(null);
                       }}
                      >
                        {/* Header de la review */}
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginBottom: theme.spacing.sm
                        }}>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: theme.spacing.sm
                          }}>
                            <span style={{
                              color: theme.colors.textSecondary,
                              fontFamily: theme.fonts.body,
                              fontSize: theme.fonts.sizeSmall
                            }}>
                              Por: {/* Solo mostrar la fecha, no el reviewer */}
                              {formatReviewDate(review.createdAt)}
                            </span>
                          </div>
                        </div>

                        {/* Comentario */}
                        <div style={{
                          marginBottom: theme.spacing.sm,
                          padding: theme.spacing.sm,
                          backgroundColor: theme.colors.background,
                          borderRadius: theme.borderRadius.input,
                          fontFamily: theme.fonts.body,
                          fontSize: theme.fonts.sizeBody,
                          lineHeight: '1.5'
                        }}>
                          "{review.comment}"
                        </div>

                        {/* Etiquetas */}
                        <div style={{ marginBottom: theme.spacing.sm }}>
                          {/* Etiquetas positivas */}
                          {review.positiveTags.length > 0 && (
                            <div style={{ marginBottom: '8px' }}>
                              {review.positiveTags.map(tag => (
                                <span key={tag} style={getTagStyle(tag, true)}>
                                  {tag}
                                </span>
                              ))}
                            </div>
                          )}

                          {/* Etiquetas negativas */}
                          {review.negativeTags.length > 0 && (
                            <div style={{ marginBottom: '8px' }}>
                              {review.negativeTags.map(tag => (
                                <span key={tag} style={getTagStyle(tag, false)}>
                                  {tag}
                                </span>
                              ))}
                            </div>
                          )}

                          {/* Tags personalizados positivos */}
                          {review.customPositiveTags && review.customPositiveTags.length > 0 && (
                            <div style={{ marginBottom: '8px' }}>
                              {review.customPositiveTags.map(tag => (
                                <span key={tag} style={{
                                  backgroundColor: '#00BFA5',
                                  color: '#FFFFFF',
                                  padding: '6px 10px',
                                  borderRadius: '12px',
                                  fontSize: '12px',
                                  fontWeight: 'bold',
                                  border: '1px solid #FFFFFF'
                                }}>
                                  {tag}
                                </span>
                              ))}
                            </div>
                          )}

                          {/* Tags personalizados negativos */}
                          {review.customNegativeTags && review.customNegativeTags.length > 0 && (
                            <div style={{ marginBottom: '8px' }}>
                              {review.customNegativeTags.map(tag => (
                                <span key={tag} style={{
                                  backgroundColor: '#D32F2F',
                                  color: '#FFFFFF',
                                  padding: '6px 10px',
                                  borderRadius: '12px',
                                  fontSize: '12px',
                                  fontWeight: 'bold',
                                  border: '1px solid #FFFFFF'
                                }}>
                                  {tag}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Reacciones */}
                        <div style={{
                          borderTop: `1px solid ${theme.colors.border}`,
                          marginTop: theme.spacing.sm,
                          paddingTop: theme.spacing.sm,
                          position: 'relative',
                          overflow: 'visible'
                        }}>
                          <ReviewReactions
                            reviewId={review.id}
                            matchId={matchId}
                            reactions={reactionsMap[review.id]?.reactions || review.reactions}
                            totalReactions={reactionsMap[review.id]?.totalReactions || review.totalReactions || 0}
                            onReactionsUpdate={(reactions: ReviewReactionsType, totalReactions: number) => 
                              handleReactionsUpdate(review.id, reactions, totalReactions)
                            }
                            isHovered={hoveredReviewId === review.id}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
                 ) : (
           <div style={{
             textAlign: 'center',
             padding: theme.spacing.lg,
             color: theme.colors.textSecondary,
             fontFamily: theme.fonts.body
           }}>
             Error cargando reviews
           </div>
        )}

        {/* Botón cerrar */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          marginTop: theme.spacing.lg
        }}>
          <button
            onClick={onClose}
            style={{
              padding: `${theme.spacing.sm} ${theme.spacing.lg}`,
              backgroundColor: theme.colors.primary,
              color: theme.colors.white,
              border: 'none',
              borderRadius: theme.borderRadius.button,
              fontFamily: theme.fonts.body,
              fontSize: theme.fonts.sizeBody,
              fontWeight: theme.fonts.weightBold,
              cursor: 'pointer'
            }}
          >
            Cerrar
          </button>
        </div>

        {/* Notificación */}
        {notification.isVisible && (
          <button type="button" onClick={hideNotification} style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            padding: '16px 20px',
            borderRadius: '8px',
            color: 'white',
            fontWeight: 'bold',
            fontSize: '14px',
            zIndex: 1001,
            maxWidth: '400px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            cursor: 'pointer',
            border: 'none',
            backgroundColor: notification.type === 'success' ? theme.colors.success :
                           notification.type === 'error' ? theme.colors.danger :
                           notification.type === 'warning' ? theme.colors.warning : theme.colors.primary
          }}>
            <span>{notification.type === 'success' ? '✅' : notification.type === 'error' ? '❌' : notification.type === 'warning' ? '⚠️' : 'ℹ️'}</span>
            <span>{notification.message}</span>
          </button>
        )}
      </div>
    </div>
  );
}; 